import React, { FC, Fragment } from 'react';
import { Box, Grid, Typography, useTheme } from '@material-ui/core';
import useContentMgmt, {
  PageTextContentType,
} from '../../../hooks/useContentMgmt';
import microcopyGroupIds from '../../pge-plus-common/microcopyGroupIds';
import { subText1, boldText, subTextWithoutMargin } from './quoteCss';
import {
  Application,
  BreakerAmperageRating,
  Job,
  JobDetails,
  LineItemCost,
  LineItemDetail,
  LineItemProduct,
  LineItemRebate,
  PanelDistance,
  PanelLocation,
  PanelSideLocation,
  Quote,
  SectionEv,
  SectionEvChargerSelection,
  SectionEvPanelInfo,
  SectionEvRebates,
} from '../../../__generated__/pgeplus-types';
import { LINE_ITEM_KEY } from '../quote.types';
import { toCurrencyString } from '../../../util/format';

interface Props {
  jobData: JobDetails | Quote;
  applicationData: Application & {
    details: [
      SectionEv &
        SectionEvPanelInfo &
        SectionEvChargerSelection &
        SectionEvRebates,
    ];
  };
  content: PageTextContentType;
  totalInstallationCost: number;
}

const JobInstallationCostDisplay: FC<Props> = ({
  jobData,
  applicationData,
  content,
  totalInstallationCost,
}: Props) => {
  const { inlineRichText } = useContentMgmt(
    microcopyGroupIds.PGE_PLUS_INSTALLER_COMPLETE_QUOTE,
  );
  const theme = useTheme();
  const hr = {
    border: '1px solid #D7D7D7',
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
  };

  const PANEL_LOCATION = {
    [PanelLocation.GarageAttached]: content.get('ATTACHED_GARAGE'),
    [PanelLocation.Basement]: content.get('BASEMENT'),
    [PanelLocation.Inside]: content.get('INSIDE_THE_HOME'),
    [PanelLocation.Outside]: content.get('OUTSIDE_THE_HOME'),
    [PanelLocation.NotSure]: content.get('NOT_SURE'),
    [PanelLocation.Other]: content.get('OTHER'),
  };

  const DISTANCE_TO_PANEL = {
    [PanelDistance.Lt_5]: content.get('REUSABLE_DISTANCE_5_FEET_OR_LESS'),
    [PanelDistance.Btw_6_30]: content.get('REUSABLE_DISTANCE_6_TO_30'),
    [PanelDistance.Gt_30]: content.get('MORE_THAN_30_FEET'),
  };

  const PANEL_SIDE_LOCATION = {
    [PanelSideLocation.Left]: content.get('REUSABLE_LEFT'),
    [PanelSideLocation.BackWall]: content.get('REUSABLE_BACK'),
    [PanelSideLocation.Right]: content.get('REUSABLE_RIGHT'),
  };

  const MAIN_BREAKER_AMPS = {
    [BreakerAmperageRating.Unknown]: content.get('IM_NOT_SURE'),
    [BreakerAmperageRating.Amps_320]: content.get('320_AMPS'),
    [BreakerAmperageRating.Amps_200]: content.get('200_AMPS'),
    [BreakerAmperageRating.Amps_150]: content.get('150_AMPS'),
    [BreakerAmperageRating.Amps_125]: content.get('125_AMPS'),
    [BreakerAmperageRating.Amps_100]: content.get('100_AMPS'),
    [BreakerAmperageRating.NoMainBreaker]: content.get('NO_MAIN_BREAKER'),
  };

  const totalInstallation = {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: theme.spacing(2),
  };
  const lineItemDetailList = jobData?.data?.lineItems?.filter(
    (item: any) => item.__typename === 'LineItemDetail',
  ) as LineItemDetail[];
  const lineItemProductList = jobData?.data?.lineItems?.filter(
    (item: any) => item.__typename === 'LineItemProduct',
  ) as LineItemProduct[];
  const lineItemCostList = jobData?.data?.lineItems?.filter(
    (item: any) => item.__typename === 'LineItemCost',
  ) as LineItemCost[];
  const lineItemRebateList = jobData?.data?.lineItems?.filter(
    (item: any) => item.__typename === 'LineItemRebate',
  ) as LineItemRebate[];
  const rebateSection =
    applicationData &&
    (applicationData?.details?.filter(
      detail => detail.sectionType === 'EV_REBATES',
    )[0] as SectionEvRebates);
  const chargerModal =
    jobData &&
    jobData.data?.lineItems?.filter(
      item => item.key === LINE_ITEM_KEY.EV_CHARGER_MODEL,
    )[0].name;
  return (
    <Fragment>
      <Grid container item xs={12} css={{ rowGap: 20 }}>
        <Grid item xs={12}>
          <Typography variant="h4" css={{ ...subText1 }}>
            {content.get('JOB')}
          </Typography>
          <Box css={{ marginTop: theme.spacing(2) }}>
            <Typography css={{ ...subTextWithoutMargin }}>
              {content.get('JOB_TYPE')}
            </Typography>
            <Typography css={{ ...subTextWithoutMargin, ...boldText }}>
              {jobData &&
                jobData &&
                (jobData.data.lineItems?.filter(
                  (item: any) => item.key === LINE_ITEM_KEY.JOB_TYPE,
                )?.[0] as any)?.value.split('  - ')[0]}
            </Typography>
          </Box>
        </Grid>
      </Grid>
      <hr css={{ ...hr }} />
      <Grid container item xs={12} css={{ rowGap: 20 }}>
        <Grid item xs={12}>
          <Typography variant="h4" css={{ ...subText1 }}>
            {content.get('REUSABLE_CHARGER_INSTALLATION_LOCATION')}
          </Typography>
          <Box css={{ marginTop: theme.spacing(2) }}>
            <Typography css={{ ...subTextWithoutMargin }}>
              {content.get('REUSABLE_MAIN_BREAKER_PANEL_LOCATION')}
            </Typography>
            <Typography css={{ ...subTextWithoutMargin, ...boldText }}>
              {lineItemDetailList &&
                PANEL_LOCATION[
                  (lineItemDetailList.find(
                    item =>
                      item?.key === LINE_ITEM_KEY.PANEL_LOCATION_MAIN_BREAKER,
                  ) as LineItemDetail)?.value as keyof typeof PANEL_LOCATION
                ]}
            </Typography>
          </Box>
          <Box css={{ marginTop: theme.spacing(2) }}>
            <Typography css={{ ...subTextWithoutMargin }}>
              {content.get('DISTANCE_FROM_CHARGER_TO_PANEL')}
            </Typography>
            <Typography css={{ ...subTextWithoutMargin, ...boldText }}>
              {lineItemDetailList &&
                DISTANCE_TO_PANEL[
                  lineItemDetailList.find(
                    item =>
                      item.key === LINE_ITEM_KEY.PREFERRED_INSTALL_DISTANCE,
                  )?.value as keyof typeof DISTANCE_TO_PANEL
                ]}
            </Typography>
          </Box>
          <Box css={{ marginTop: theme.spacing(2) }}>
            <Typography css={{ ...subTextWithoutMargin }}>
              {content.get('WILL_PANEL_BE_INSTALLED_WITHIN_DISTANCE')}
            </Typography>
            <Typography css={{ ...subTextWithoutMargin, ...boldText }}>
              {lineItemDetailList &&
                lineItemDetailList.filter(
                  item =>
                    item.key ===
                    LINE_ITEM_KEY.CHARGER_INSTALL_WITHIN_PANEL_DISTANCE,
                )?.[0]?.value}
            </Typography>
          </Box>
        </Grid>
      </Grid>
      <hr css={{ ...hr }} />
      <Grid container item xs={12} css={{ rowGap: 20 }}>
        <Grid item xs={12}>
          <Typography variant="h4" css={{ ...subText1 }}>
            {content.get('MAIN_ELECTRICAL_BREAKER_PANEL_DETAILS')}
          </Typography>
          <Box css={{ marginTop: theme.spacing(2) }}>
            <Typography
              css={{ ...subTextWithoutMargin, fontWeight: boldText.fontWeight }}
            >
              {content.get('REPLACE_PANEL_QUESTION_HEADER')}
            </Typography>
            <Box css={{ ...subTextWithoutMargin, marginTop: theme.spacing(1) }}>
              {inlineRichText(content.get('REPLACE_PANEL_TWO_BULLETS'))}
            </Box>
            <Typography
              css={{
                ...subTextWithoutMargin,
                ...boldText,
                marginTop: theme.spacing(1),
              }}
            >
              {lineItemDetailList &&
                lineItemDetailList.filter(
                  item => item.key === LINE_ITEM_KEY.PANEL_UPGRADE,
                )?.[0]?.value}
            </Typography>
          </Box>
          <Box css={{ marginTop: theme.spacing(2) }}>
            <Typography css={{ ...subTextWithoutMargin }}>
              {content.get('CONFIRM_AMPERAGE_MAIN_BREAKER')}
            </Typography>
            <Typography css={{ ...subTextWithoutMargin, ...boldText }}>
              {lineItemDetailList &&
                MAIN_BREAKER_AMPS[
                  lineItemDetailList.filter(
                    item => item.key === LINE_ITEM_KEY.PANEL_AMPERAGE,
                  )?.[0]?.value as keyof typeof MAIN_BREAKER_AMPS
                ]}
            </Typography>
          </Box>
        </Grid>
      </Grid>
      <hr css={{ ...hr }} />
      <Grid container item xs={12} css={{ rowGap: 20 }}>
        <Grid item xs={12}>
          <Typography variant="h4" css={{ ...subText1 }}>
            {content.get('CHARGER_EQUIPMENT')}
          </Typography>
          <Box css={{ marginTop: theme.spacing(2) }}>
            <Typography css={{ ...subTextWithoutMargin }}>
              {content.get('EV_CHARGER_MODEL')}
            </Typography>
            <Typography css={{ ...subTextWithoutMargin, ...boldText }}>
              {chargerModal}
            </Typography>
          </Box>
        </Grid>
      </Grid>
      <hr css={{ ...hr }} />
      <Grid container item xs={12} css={{ rowGap: 20 }}>
        <Grid item xs={12}>
          <Typography variant="h4" css={{ ...subText1 }}>
            {content.get('COSTS_CHARGER')}
          </Typography>
          <Box css={{ marginTop: theme.spacing(2) }}>
            <Typography css={{ ...subTextWithoutMargin }}>
              {content.get('EV_CHARGER_COST')}
            </Typography>
            <Typography css={{ ...subTextWithoutMargin, ...boldText }}>
              $
              {toCurrencyString(
                (lineItemProductList &&
                  lineItemProductList?.filter(
                    item => item.key === LINE_ITEM_KEY.EV_CHARGER_MODEL,
                  )[0].amount) ||
                  0,
                true,
              )}
            </Typography>
          </Box>
        </Grid>
      </Grid>
      <hr css={{ ...hr }} />
      <Grid container item xs={12} css={{ rowGap: 20 }}>
        <Grid item xs={12}>
          <Typography variant="h4" css={{ ...subText1 }}>
            {content.get('COSTS_INSTALLATION')}
          </Typography>
          <Box css={{ marginTop: theme.spacing(2) }}>
            <Typography css={{ ...subTextWithoutMargin }}>
              {content.get('INSTALLATION_AND_MATERIALS')}
            </Typography>
            <Typography css={{ ...subTextWithoutMargin, ...boldText }}>
              $
              {toCurrencyString(
                (lineItemCostList &&
                  lineItemCostList.filter(
                    item => item.key === LINE_ITEM_KEY.INSTALLATION_MATERIALS,
                  )[0].amount) ||
                  0,
                true,
              )}
            </Typography>
          </Box>
          <Box css={{ marginTop: theme.spacing(2) }}>
            <Typography css={{ ...subTextWithoutMargin }}>
              {content.get('REUSABLE_PERMITS')}
            </Typography>
            <Typography css={{ ...subTextWithoutMargin, ...boldText }}>
              $
              {toCurrencyString(
                (lineItemCostList &&
                  lineItemCostList.filter(
                    item => item.key === LINE_ITEM_KEY.PERMITS,
                  )[0].amount) ||
                  0,
                true,
              )}
            </Typography>
          </Box>
          <Box css={{ marginTop: theme.spacing(2) }}>
            <Typography css={{ ...subTextWithoutMargin }}>
              {content.get('REUSABLE_TRAVEL_TIME')}
            </Typography>
            <Typography css={{ ...subTextWithoutMargin, ...boldText }}>
              $
              {toCurrencyString(
                (lineItemCostList &&
                  lineItemCostList.filter(
                    item => item.key === LINE_ITEM_KEY.TRAVEL_TIME,
                  )[0].amount) ||
                  0,
                true,
              )}
            </Typography>
          </Box>
          <Box css={{ marginTop: theme.spacing(2) }}>
            <Typography css={{ ...subTextWithoutMargin }}>
              {content.get('REUSABLE_FEES')}
            </Typography>
            <Typography css={{ ...subTextWithoutMargin, ...boldText }}>
              $
              {toCurrencyString(
                (lineItemCostList &&
                  lineItemCostList.filter(
                    item => item.key === LINE_ITEM_KEY.FEES,
                  )[0].amount) ||
                  0,
                true,
              )}
            </Typography>
          </Box>
          <Box css={{ marginTop: theme.spacing(2) }}>
            <Typography css={{ ...subTextWithoutMargin }}>
              {content.get('REUSABLE_MISC_WORK')}
            </Typography>
            <Typography css={{ ...subTextWithoutMargin, ...boldText }}>
              $
              {toCurrencyString(
                (lineItemCostList &&
                  lineItemCostList.filter(
                    item => item.key === LINE_ITEM_KEY.MISCELLANEOUS_WORK,
                  )[0].amount) ||
                  0,
                true,
              )}
            </Typography>
          </Box>
          <Box css={{ ...totalInstallation }}>
            <Typography css={{ ...subText1 }}>
              {content.get('TOTAL_INSTALLATION_COSTS')}
            </Typography>
            <Typography css={{ ...subText1 }}>
              ${toCurrencyString(totalInstallationCost, true)}
            </Typography>
          </Box>
        </Grid>
      </Grid>
      <hr css={{ ...hr }} />
      <Grid container item xs={12} css={{ rowGap: 20 }}>
        <Grid item xs={12}>
          <Typography css={{ ...subTextWithoutMargin }}>
            {content.get('ADDITIONAL_COMMENTS')}
          </Typography>
          <Box>
            <Typography css={{ ...subTextWithoutMargin, ...boldText }}>
              {lineItemDetailList &&
                lineItemDetailList.filter(
                  item => item.key === LINE_ITEM_KEY.COMMENTS,
                )?.[0]?.value}
            </Typography>
          </Box>
        </Grid>
      </Grid>
      <hr css={{ ...hr }} />
      <Grid container item xs={12} css={{ rowGap: 20 }}>
        <Grid item xs={12}>
          <Box>
            <Typography variant="h4" css={{ ...subText1 }}>
              {content.get('REBATE_SECTION')}
            </Typography>
          </Box>
          <Box css={{ marginTop: theme.spacing(2) }}>
            <Typography css={{ ...subTextWithoutMargin }}>
              {content.get('REBATE_ELIGIBILITY')}:
            </Typography>
            <Typography css={{ ...subTextWithoutMargin, ...boldText }}>
              {rebateSection && rebateSection?.data?.rebateEligibility?.eligible
                ? content.get('REUSABLE_YES')
                : content.get('REUSABLE_NO')}
            </Typography>
          </Box>
          <Box css={{ marginTop: theme.spacing(2) }}>
            <Typography css={{ ...subTextWithoutMargin }}>
              {content.get('TYPE_OF_REBATE')}
            </Typography>
            <Typography css={{ ...subTextWithoutMargin, ...boldText }}>
              {(lineItemRebateList && lineItemRebateList[0]?.name) || 'N/A'}
            </Typography>
          </Box>
          <Box css={{ marginTop: theme.spacing(2) }}>
            <Typography css={{ ...subTextWithoutMargin }}>
              {content.get('PANEL_UPGRADE_COLON')}
            </Typography>
            <Typography css={{ ...subTextWithoutMargin, ...boldText }}>
              {(lineItemRebateList?.[0]?.rebateItems?.panelUpgrade ?? 0) > 0
                ? content.get('REUSABLE_YES')
                : content.get('REUSABLE_NO')}
            </Typography>
          </Box>
        </Grid>
      </Grid>
    </Fragment>
  );
};

export default JobInstallationCostDisplay;
